import { AfterViewInit, Component, CUSTOM_ELEMENTS_SCHEMA, Input, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { AuthPolicies } from '../../../auth/auth-policies';
import { ContainerGradeRepairCodeAdvancedFilterComponent } from '../container-grade-repair-code-advanced-filter/container-grade-repair-code-advanced-filter.component';
import { CommonModule } from '@angular/common';
import '@maersk-global/mds-components-core/mc-tag';
import '@maersk-global/mds-components-core/mc-modal';
import '@maersk-global/mds-components-core/mc-tooltip';
import '@maersk-global/mds-components-core/mc-loading-indicator';
import { ToastrService } from '../../../services/toastr-service';
import { TagWithOverflowComponent } from '../../../shared-components/tag-with-overflow/tag-with-overflow.component';
import { ContainerGradeRepairCodeFilterForm } from '../../../form-model/container-grade-repair-code-filter.form.model';
import { ContainerGradeRepairCodeSearchTextForm } from '../../../form-model/container-grade-repair-code-search-text.form.model';
import { environment } from '../../../../environments/environment';
import { ContainerGradeRepairCodeService } from '../../../services/container-grade-repair-code.service';
import { DownloadFilterCriteria, toFilter, toEqualsFilter, toInFilter } from '../../../models/download-filter.model';
import { getFilterValue as getSharedFilterValue, removeFilter as removeSharedFilter } from '../../../helper/filter-helpers';
import { downloadBlob, handleDownloadError, generateDateFilename } from '../../../helper/download-helpers';

type FilterValue = string | string[] | boolean[];

@Component({
  selector: 'app-container-grade-repair-code-filters',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    ContainerGradeRepairCodeAdvancedFilterComponent,
    TagWithOverflowComponent,
  ],
  templateUrl: './container-grade-repair-code-filters.component.html',
  styleUrl: './container-grade-repair-code-filters.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ContainerGradeRepairCodeFiltersComponent implements AfterViewInit {
  showAdvanceFilters = false;
  isDownload = false;
  canAddNew = this.authPolicies.canAddContainerGradeRepairCode && environment.Features.EnableContainerGradeRepairCode;
  @Input({ required: true }) filterFormGroup!: FormGroup<{
    filterForm: FormGroup<ContainerGradeRepairCodeFilterForm>;
    searchTextForm: FormGroup<ContainerGradeRepairCodeSearchTextForm>;
  }>;
  taggedFilters: { key: string; value: FilterValue }[] = [];

  @ViewChild(ContainerGradeRepairCodeAdvancedFilterComponent)
  advanceFilterComponent!: ContainerGradeRepairCodeAdvancedFilterComponent;

  constructor(
    public authPolicies: AuthPolicies,
    private readonly formBuilder: FormBuilder,
    private readonly toastrService: ToastrService,
    private readonly router: Router,
    private readonly service: ContainerGradeRepairCodeService,
  ) {}

  ngAfterViewInit(): void {
    this.ensureFlagControlsExist();
    this.subscribeToFormChanges();
    this.setFilterFormValue();
  }

  private ensureFlagControlsExist(): void {
    const filterForm = this.filterFormGroup.controls.filterForm;
    const searchForm = this.filterFormGroup.controls.searchTextForm;
    if (!filterForm.get('applyFilter')) {
      filterForm.addControl('applyFilter', this.formBuilder.control<boolean | null>(null));
    }
    if (!searchForm.get('appliedSearchTextFilter')) {
      searchForm.addControl('appliedSearchTextFilter', this.formBuilder.control<boolean | null>(null));
    }
    if (!filterForm.get('RepairCodeList')) {
      filterForm.addControl('RepairCodeList', this.formBuilder.control<string[] | null>(null));
    }
    if (!filterForm.get('RepairModeList')) {
      filterForm.addControl('RepairModeList', this.formBuilder.control<string[] | null>(null));
    }
    if (!filterForm.get('GradesApplicableList')) {
      filterForm.addControl('GradesApplicableList', this.formBuilder.control<string[] | null>(null));
    }
    if (!filterForm.get('GenerateGradeChange')) {
      filterForm.addControl('GenerateGradeChange', this.formBuilder.control<string | null>(null));
    }
  }

  private setFilterFormValue(): void {
    const filterValue = this.service.getFilterFormValue();
    if (filterValue) {
      this.filterFormGroup.controls.filterForm.patchValue(filterValue, { emitEvent: false });
      this.updateSelectedFiltersSection();
      setTimeout(() => {
        this.filterFormGroup.controls.filterForm.get('applyFilter')?.setValue(true);
      });
    }
  }

  private subscribeToFormChanges(): void {
    this.filterFormGroup.controls.filterForm.get('applyFilter')!.valueChanges.subscribe((value) => {
      if (value) {
        this.filterFormGroup.controls.filterForm.get('applyFilter')!.setValue(null, { emitEvent: false });
        this.showAdvanceFilters = false;
        this.updateSelectedFiltersSection();
        this.service.setFilterFormValue(this.filterFormGroup.controls.filterForm.getRawValue());
      }
    });
    this.filterFormGroup.controls.searchTextForm.get('appliedSearchTextFilter')!.valueChanges.subscribe((_) => {
      this.updateSelectedFiltersSection();
    });
  }

  unselectRepairCode(): (_: string) => void {
    return (code: string) => {
      const key = this.taggedFilters.find((x) => x.key === 'Repair Code');
      const control = this.filterFormGroup.controls.filterForm.get('RepairCodeList');
      const currentValues = key?.value as string[];
      const updatedValue = currentValues.filter((c) => c !== code);
      if (key) {
        key.value = updatedValue;
      }
      control?.setValue(updatedValue.length ? updatedValue : null);
      this.filterFormGroup.controls.filterForm.get('applyFilter')?.setValue(true);
      this.updateSelectedFiltersSection();
    };
  }

  unselectMode(): (_: string) => void {
    return (mode: string) => {
      const key = this.taggedFilters.find((x) => x.key === 'Mode');
      const control = this.filterFormGroup.controls.filterForm.get('RepairModeList');
      const currentValues = key?.value as string[];
      const updatedValue = currentValues.filter((m) => m !== mode);
      if (key) {
        key.value = updatedValue;
      }
      control?.setValue(updatedValue.length ? updatedValue : null);
      this.filterFormGroup.controls.filterForm.get('applyFilter')?.setValue(true);
      this.updateSelectedFiltersSection();
    };
  }

  unselectGrade(grade: string): void {
    const currentValues: string[] = this.filterFormGroup.controls.filterForm.get('GradesApplicableList')?.value || [];
    const updatedValues = currentValues.filter((g) => g !== grade);
    this.filterFormGroup.controls.filterForm.get('GradesApplicableList')?.setValue(updatedValues.length ? updatedValues : null);
    this.filterFormGroup.controls.filterForm.get('applyFilter')?.setValue(true);
    this.updateSelectedFiltersSection();
  }

  unselectGenerateGradeChange(): void {
    this.filterFormGroup.controls.filterForm.get('GenerateGradeChange')?.setValue(null);
    this.filterFormGroup.controls.filterForm.get('applyFilter')?.setValue(true);
    this.updateSelectedFiltersSection();
  }

  showAdvanceFiltersModal(): void {
    this.showAdvanceFilters = true;
  }

  onDownload(): void {
    this.isDownload = true;
    const filters = this.buildDownloadFilters();
    this.service.downloadToExcel({ columns: [], filters }).subscribe({
      next: (blob) => {
        downloadBlob(blob, generateDateFilename('repair-code-grade-mapping'));
        this.isDownload = false;
      },
      error: (err) => {
        handleDownloadError(err, this.toastrService);
        this.isDownload = false;
      },
    });
  }

  private buildDownloadFilters(): DownloadFilterCriteria[] {
    const filters: DownloadFilterCriteria[] = [];
    const form = this.filterFormGroup.controls.filterForm;

    const codes = form.controls.RepairCodeList?.value;
    if (codes && codes.length > 0) {
      filters.push(toFilter('repair_code', codes));
    }

    const modes = form.controls.RepairModeList?.value;
    if (modes && modes.length > 0) {
      filters.push(toFilter('repair_mode', modes));
    }

    const grades = form.controls.GradesApplicableList?.value;
    if (grades && grades.length > 0) {
      filters.push(toInFilter('container_grade_code', grades), toEqualsFilter('is_applicable', 'true'));
    }

    const generateGradeChange = form.controls.GenerateGradeChange?.value;
    if (generateGradeChange === 'yes') {
      filters.push(toEqualsFilter('changes_grade', 'true'));
    } else if (generateGradeChange === 'no') {
      filters.push(toEqualsFilter('changes_grade', 'false'));
    }

    return filters;
  }

  closeAdvanceFiltersModal(): void {
    if (this.showAdvanceFilters) {
      this.showAdvanceFilters = false;
      const lastApplied = this.service.getFilterFormValue();
      const form = this.filterFormGroup.controls.filterForm;
      form.patchValue(
        {
          RepairCodeList: lastApplied?.RepairCodeList ?? null,
          RepairModeList: lastApplied?.RepairModeList ?? null,
          GradesApplicableList: lastApplied?.GradesApplicableList ?? null,
          GenerateGradeChange: lastApplied?.GenerateGradeChange ?? null,
        },
        { emitEvent: false },
      );
    }
    this.updateSelectedFiltersSection();
  }

  clearAllSelectedFilters(): void {
    this.filterFormGroup.reset();
    this.filterFormGroup.controls.filterForm.get('applyFilter')?.setValue(true);
    this.service.setFilterFormValue(null);
    this.updateSelectedFiltersSection();
  }

  updateSelectedFiltersSection(): void {
    const form = this.filterFormGroup.controls.filterForm;
    this.markAsTagged('Repair Code', form.get('RepairCodeList')?.value ?? null);
    this.markAsTagged('Mode', form.get('RepairModeList')?.value ?? null);
    this.markAsTagged('Grades', form.get('GradesApplicableList')?.value ?? null);
    const ggc = form.get('GenerateGradeChange')?.value;
    let tagValue: string | null;
    if (ggc === 'yes') {
      tagValue = 'Yes';
    } else if (ggc === 'no') {
      tagValue = 'No';
    } else {
      tagValue = null;
    }
    this.markAsTagged('Generate Grade Change', tagValue);
  }

  markAsTagged(key: string, newValue: FilterValue | null): void {
    const currentValue = this.getFilterValue(key);
    if (currentValue !== newValue) {
      this.removeFilter(key);
      if (newValue !== null && newValue !== '' && (!Array.isArray(newValue) || newValue.length > 0)) {
        this.taggedFilters.unshift({ key, value: newValue });
      }
    }
  }

  removeFilter(key: string): void {
    removeSharedFilter(this.taggedFilters, key);
  }

  getFilterValue(key: string): FilterValue | null {
    return getSharedFilterValue(this.taggedFilters, key) as FilterValue | null;
  }

  getTaggedRepairCodes(): string[] {
    const filter = this.taggedFilters.find((f) => f.key === 'Repair Code');
    return filter && Array.isArray(filter.value) ? (filter.value as string[]) : [];
  }

  getTaggedModes(): string[] {
    const filter = this.taggedFilters.find((f) => f.key === 'Mode');
    return filter && Array.isArray(filter.value) ? (filter.value as string[]) : [];
  }

  getTaggedGrades(): string[] {
    const filter = this.taggedFilters.find((f) => f.key === 'Grades');
    return filter && Array.isArray(filter.value) ? (filter.value as string[]) : [];
  }

  isArrayType(value: FilterValue): boolean {
    return Array.isArray(value);
  }

  onAddNewClicked(): void {
    this.router.navigate(['/master-data/container-grade-repair-code/add-new']);
  }
}
